import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPlay, faPause, faCheck } from '@fortawesome/free-solid-svg-icons';
import careManagementService from '../../services/careManagementService';
import patientService from '../../services/patientService';
import practiceService from '../../services/practiceService';
import settingsAPI from '../../services/settingsService';
import { toast } from 'react-toastify';
import './AddFollowUpModal.css';

const AddFollowUpModal = ({ isOpen, onClose, practiceId, patientId, patientName, onSuccess, initialTimerValue = 0 }) => {
  const [formData, setFormData] = useState({
    serviceType: 'General',
    followUpStatus: 'Continue follow up',
    assignTo: '',
    serviceTime: '',
    manualMinutes: '00',
    manualSeconds: '00',
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().split(' ')[0].substring(0, 5),
    template: '',
    content: ''
  });

  const [timer, setTimer] = useState(initialTimerValue);
  const [isRunning, setIsRunning] = useState(false);
  const [followUpNotes, setFollowUpNotes] = useState([]);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [contentError, setContentError] = useState('');
  const timerIntervalRef = useRef(null);

  // Options
  const serviceTypeOptions = ['General', 'Call via others'];
  const statusOptions = ['Continue follow up', 'No need to follow up'];
  useEffect(() => {
    if (isOpen) {
      setFormData({
        serviceType: 'General',
        followUpStatus: 'Continue follow up',
        assignTo: '',
        serviceTime: '',
        manualMinutes: '00',
        manualSeconds: '00',
        date: new Date().toISOString().split('T')[0],
        time: new Date().toTimeString().split(' ')[0].substring(0, 5),
        template: '',
        content: ''
      });
      setTimer(initialTimerValue);
      setIsRunning(true); // Auto-start as well to match "starts as"
      setContentError('');
      fetchTemplates();
      fetchFollowUpNotes();
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }
  }, [isOpen, practiceId, patientId]);

  const fetchTemplates = async () => {
    try {
      const response = await settingsAPI.getFollowUpTemplates();
      if (response?.success) {
        setTemplateOptions(Array.isArray(response.data) ? response.data : []);
      } else {
        setTemplateOptions([]);
      }
    } catch (error) {
      setTemplateOptions([]);
      console.error('Error fetching follow-up templates:', error);
    }
  };

  useEffect(() => {
    if (isRunning) {
      timerIntervalRef.current = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }
    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    };
  }, [isRunning]);

  const fetchFollowUpNotes = async () => {
    if (!practiceId || !patientId) return;
    try {
      setLoading(true);
      const response = await patientService.getFollowUps(practiceId, patientId);
      if (response.data && response.data.success) {
        setFollowUpNotes(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching follow-up notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTimerDigits = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleManualTimeChange = (field, value) => {
    const digitsOnly = String(value ?? '').replace(/\D/g, '').substring(0, 2);
    const normalizedValue = field === 'manualSeconds'
      ? (digitsOnly === '' ? '' : String(Math.min(59, parseInt(digitsOnly, 10))))
      : digitsOnly;

    setFormData((prev) => {
      const next = { ...prev, [field]: normalizedValue };
      const minutes = parseInt(next.manualMinutes || '0', 10) || 0;
      const secondsRaw = parseInt(next.manualSeconds || '0', 10) || 0;
      const seconds = Math.min(59, Math.max(0, secondsRaw));
      setTimer((minutes * 60) + seconds);
      return next;
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'content' && String(value || '').trim()) {
      setContentError('');
    }
  };

  const handleTemplateChange = (templateId) => {
    const selected = templateOptions.find((t) => String(t.id) === String(templateId));
    setFormData((prev) => ({
      ...prev,
      template: templateId,
      content: selected?.template || ''
    }));
    setContentError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.content.trim()) {
      setContentError('Content is required');
      return;
    }
    setContentError('');

    setSubmitting(true);
    try {
      const totalSeconds = parseInt(formData.manualMinutes || 0) * 60 + parseInt(formData.manualSeconds || 0);
      const followUpData = {
        content: formData.content,
        service_type: formData.serviceType,
        follow_up_status: formData.followUpStatus.toLowerCase().replace(/\s+/g, '_'),
        assign_to: formData.assignTo || null,
        service_time: formData.serviceTime || null,
        service_time_seconds: totalSeconds,
        date: formData.date,
        time: formData.time,
        template_id: formData.template || null
      };

      const response = await careManagementService.addFollowUp(practiceId, patientId, followUpData);
      if (response.data?.success) {
        if (practiceId) {
          practiceService.invalidatePracticePatientsCache(practiceId);
        }
        toast.success('Follow-up recorded successfully');
        onSuccess?.();
        onClose();
      } else {
        toast.error(response.data?.message || 'Failed to save follow-up');
      }
    } catch (error) {
      toast.error('Error saving follow-up');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDateMDY = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const d = date.getDate().toString().padStart(2, '0');
    const y = date.getFullYear();
    return `${m}-${d}-${y}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fu-redesign-overlay" onClick={onClose}>
      <div className="fu-redesign-container" onClick={(e) => e.stopPropagation()}>
        <button className="fu-redesign-close-btn-circle" onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>

        <header className="fu-redesign-header-clean">
          <div className="fu-header-titles">
            <h2 className="fu-redesign-title">Add Follow Up</h2>
          </div>
        </header>

        <div className="fu-redesign-grid">
          <div className="fu-redesign-form-col">
            <div className="fu-form-bordered-box">
              <h1 className="fu-form-legend-name">{patientName || 'Patient'}</h1>
              <form onSubmit={handleSubmit}>
                {/* Row 1: Service Type, Status, Assign To */}
                <div className="fu-redesign-flex-row">
                  <div className="fu-redesign-field">
                    <label>Service Type</label>
                    <select 
                      value={formData.serviceType} 
                      onChange={e => handleInputChange('serviceType', e.target.value)}
                    >
                      {serviceTypeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                  <div className="fu-redesign-field">
                    <label>Follow Up Status:</label>
                    <select 
                      value={formData.followUpStatus} 
                      onChange={e => handleInputChange('followUpStatus', e.target.value)}
                    >
                      {statusOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                  <div className="fu-redesign-field">
                    <label>Assign To</label>
                    <select 
                      value={formData.assignTo} 
                      onChange={e => handleInputChange('assignTo', e.target.value)}
                    >
                      <option value="">Select...</option>
                    </select>
                  </div>
                </div>
 
                {/* Row 2: Service Time (Timer), Manual Mins, Manual Secs */}
                <div className="fu-redesign-flex-row align-center">
                  <div className="fu-redesign-field">
                    <label>Service Time*</label>
                    <div className="fu-timer-chip">
                      <span className="fu-timer-text">{formatTimerDigits(timer)}</span>
                      <button 
                        type="button" 
                        className="fu-timer-mini-toggle"
                        onClick={() => setIsRunning(!isRunning)}
                      >
                        {isRunning ? (
                          <div className="fu-pause-bars"><span></span><span></span></div>
                        ) : (
                          <FontAwesomeIcon icon={faPlay} style={{ color: '#ff4d4d' }} />
                        )}
                      </button>
                    </div>
                  </div>
 
                  <div className="fu-redesign-field">
                    <label>Manual Min(s) *</label>
                    <input 
                      type="number"
                      min="0"
                      step="1"
                      value={formData.manualMinutes} 
                      onChange={e => handleManualTimeChange('manualMinutes', e.target.value)}
                      placeholder="00"
                    />
                  </div>
 
                  <div className="fu-redesign-field">
                    <label>Manual Sec(s) *</label>
                    <input 
                      type="number"
                      min="0"
                      max="59"
                      step="1"
                      value={formData.manualSeconds} 
                      onChange={e => handleManualTimeChange('manualSeconds', e.target.value)}
                      placeholder="00"
                    />
                  </div>
                </div>
 
                {/* Row 3: Date, Time, Template */}
                <div className="fu-redesign-flex-row">
                  <div className="fu-redesign-field">
                    <label>Date</label>
                    <input 
                      type="date" 
                      value={formData.date} 
                      onChange={e => handleInputChange('date', e.target.value)}
                    />
                  </div>
                  <div className="fu-redesign-field">
                    <label>Time</label>
                    <input 
                      type="time" 
                      value={formData.time} 
                      onChange={e => handleInputChange('time', e.target.value)}
                    />
                  </div>
                  <div className="fu-redesign-field">
                    <label>Template</label>
                    <select 
                      value={formData.template} 
                      onChange={e => handleTemplateChange(e.target.value)}
                    >
                      <option value="">Please Choose a Template</option>
                      {templateOptions.map((opt) => (
                        <option key={opt.id} value={opt.id}>{opt.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
 
                <div className="fu-redesign-field fu-redesign-context-field">
                  <label>Content *</label>
                  <textarea 
                    placeholder="Content.."
                    value={formData.content}
                    onChange={e => handleInputChange('content', e.target.value)}
                    className={contentError ? 'fu-input-invalid' : ''}
                  />
                  {contentError && <div className="fu-field-error">{contentError}</div>}
                </div>
 
                <div className="fu-redesign-actions">
                  <button type="button" className="fu-btn-cancel" onClick={onClose}>Cancel</button>
                  <button type="submit" className="fu-btn-save" disabled={submitting}>
                    {submitting ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Right Column: Notes History */}
          <div className="fu-notes-history-col">
            <h4>Follow Up Notes</h4>
            <div className="fu-notes-container">
              {loading ? (
                <p className="fu-no-notes">Loading notes...</p>
              ) : followUpNotes.length > 0 ? (
                followUpNotes.map((note, index) => (
                  <div key={note.id || index} className="fu-note-item">
                    <div className="fu-note-header-line">
                      <span className="fu-note-author">{note.caregiver_name || 'System Caregiver'}</span>
                      <span className="fu-note-date">{formatDateMDY(note.date || note.created_at)}</span>
                    </div>
                    <p className="fu-note-text">{note.content}</p>
                  </div>
                ))
              ) : (
                <p className="fu-no-notes">No previous notes</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddFollowUpModal;
